import {
  type CreateReference,
  Currency,
  createCodeableConcept,
  createCoding,
  createExtensionValue,
  createIdentifier,
  ExtensionUrl,
  findExtensionValue,
  getDisplayFromCodableConcepts,
  getFirstCodeFromCodableConcepts,
  getIdentifier,
  IdentifierCode,
  type RequiredCoding,
  valsOrNone,
} from '@quromedical/fhir-common';
import {
  type BaseProvider,
  type BillingProvider,
  type ClaimFinancials,
  type CreateClaimRequest,
  type DiagnosisCode,
  type Patient as HealthbridgePatient,
  type LineItem,
  type Member,
  PlaceServiceCode,
  type Providers,
  type Transaction,
  type TreatingProvider,
} from '@quromedical/healthbridge/client';
import {
  compact,
  type FlatPartial,
  first,
  partial,
  sum,
} from '@quromedical/utils';
import { z } from 'zod';
import { AdmissionSchema } from '../admission';
import {
  type ChargeItemDefinitionMoney,
  isCentsFactor,
  isCurrency,
  MONEY_FACTOR,
} from '../charge-item-definition';
import {
  attachIdentifiers,
  createOptionalIdentifier,
  getValidReference,
  getValidVersionReference,
} from '../mappings';
import { type MedicalAid, MedicalAidSchema } from '../medical-aid';
import { GeneralSchema } from '../person';
import type { WithId } from '../types';
import {
  ChargeLineQuantitySchema,
  type ClaimChargeLineQuantity,
  type ClaimGetResponse,
  type Claim as ModelClaim,
  type PartialClaim,
} from './schema';

const createChargeItemReferenceExtension = createExtensionValue(
  ExtensionUrl.claimItemChargeItemDefinition,
);

// the `T` signifies where the time starts, we want everything before that
const asHealthbridgeDate = (date: string) =>
  new Date(date).toISOString().split('T')[0];

// the `.` indicates where the milliseconds start, we want everything before that
const asHealthbridgeDateTime = (date: string) =>
  new Date(date).toISOString().split('.')[0];

const mapChargeLineToClaimItem = (
  encounter: CreateReference,
  item: ClaimChargeLineQuantity,
  index: number,
): fhir4.ClaimItem => ({
  extension: [createChargeItemReferenceExtension(item.chargeItem)],
  encounter: [encounter],
  sequence: index + 1,
  quantity: {
    value: item.quantity,
  },
  factor: item.chargeLine.amount.factor,
  unitPrice: {
    currency: item.chargeLine.amount.currency,
    value: item.chargeLine.amount.value,
  },
  productOrService: {
    text: item.chargeLine.name,
    coding: [createCoding(item.chargeLine.code, item.chargeLine.name)],
  },
});

const getChargeItemReferenceValue = findExtensionValue(
  ExtensionUrl.claimItemChargeItemDefinition,
);

const getChargeItemDefinitionReference = (
  extension: fhir4.Extension[] = [],
): CreateReference | undefined => {
  const ref = getChargeItemReferenceValue(extension);

  return getValidReference(ref);
};

const mapClaimItemToChargeLine = (
  claimItem: fhir4.ClaimItem,
): ClaimChargeLineQuantity | undefined => {
  const { productOrService, unitPrice, factor, extension } = claimItem;

  const quantity = claimItem.quantity?.value;
  const code = getFirstCodeFromCodableConcepts([productOrService]);
  const name = getDisplayFromCodableConcepts([productOrService]);

  const value = unitPrice?.value;
  const currency = unitPrice?.currency;

  const isCurrencyValid = isCurrency(currency);
  const isFactorValid = isCentsFactor(factor);

  const chargeItem = getChargeItemDefinitionReference(extension);

  const isValid =
    quantity &&
    code &&
    value &&
    isCurrencyValid &&
    isFactorValid &&
    chargeItem &&
    name;

  if (!isValid) {
    return undefined;
  }

  return {
    chargeItem,
    chargeLine: {
      amount: {
        currency,
        factor,
        value,
      },
      name,
      code,
    },
    quantity,
  };
};

const createIdentifiers = (
  claim: ModelClaim,
): fhir4.Identifier[] | undefined => {
  const identifier = createOptionalIdentifier(
    IdentifierCode.claimInvoiceNumber,
    claim.invoiceNumber,
  );
  if (!identifier) {
    return undefined;
  }

  return [identifier];
};

export const mapClaimToFhir = (claim: ModelClaim): fhir4.Claim => {
  const {
    admission,
    chargeLines,
    coverage,
    created,
    patient,
    priority,
    status,
    type,
    provider,
  } = claim;

  const mapChargeLineToItem = partial(mapChargeLineToClaimItem, admission);

  const item = valsOrNone(chargeLines.map(mapChargeLineToItem));
  const identifier = createIdentifiers(claim);

  return {
    resourceType: 'Claim',
    use: 'claim',
    status,
    created,
    patient,
    priority: createCodeableConcept(priority),
    provider,
    type: createCodeableConcept(type),
    insurance: [
      {
        coverage,
        focal: true,
        sequence: 1,
      },
    ],
    item,
    identifier,
  };
};

export const mapClaimToModel = (
  claim: WithId<fhir4.Claim>,
): WithId<PartialClaim> => {
  const {
    id,
    created,
    insurance,
    patient,
    priority,
    provider,
    status,
    item = [],
    type,
  } = claim;
  const coverage = getValidVersionReference(
    ['Coverage'],
    first(insurance)?.coverage,
  );

  const admission = getValidReference(first(first(item)?.encounter));
  const chargeLines = compact(item.map(mapClaimItemToChargeLine));
  const invoiceNumber = getIdentifier(
    IdentifierCode.claimInvoiceNumber,
    claim.identifier,
  );

  return {
    id,
    created,
    status,
    patient: getValidReference(patient),
    provider: getValidReference(provider),
    coverage,
    admission,
    priority: getFirstCodeFromCodableConcepts([priority]),
    type: getFirstCodeFromCodableConcepts([type]),
    chargeLines,
    invoiceNumber,
  };
};

export const mapClaimToFhirModel = (
  claim: WithId<fhir4.Claim>,
): WithId<ClaimGetResponse> => {
  const claimResponses = mapClaimToModel(claim);

  return {
    ...claimResponses,
    responses: [],
  };
};

const isZARCents = (amount: ChargeItemDefinitionMoney) =>
  amount.currency === Currency.ZAR && amount.factor === MONEY_FACTOR;

const getChargeLineCostCents = (
  quantity: ClaimChargeLineQuantity,
): number | undefined => {
  const isAllowedAmount = isZARCents(quantity.chargeLine.amount);

  if (!isAllowedAmount) {
    return undefined;
  }

  if (quantity.quantity < 0) {
    return undefined;
  }

  return quantity.chargeLine.amount.value * quantity.quantity;
};

/**
 * Check that all items in an array are filled
 */
const isFilled = <T>(items: Array<T | undefined>): items is Array<T> => {
  const empty = items.includes(undefined);
  return !empty;
};

const getClaimFinancials = (
  chargeLines: ClaimChargeLineQuantity[],
): ClaimFinancials | undefined => {
  const totals = chargeLines.map(getChargeLineCostCents);

  const allItemsDefined = isFilled(totals);

  if (!allItemsDefined) {
    return undefined;
  }

  const total = sum(totals).toString();

  return {
    NettBilledAmt: total,
    TotalBilledAmt: total,
    TotalDiscount: '0',
  };
};

const getClaimLineItem = (
  dateOfService: string,
  diagnosisCodes: DiagnosisCode[],
  quantity: ClaimChargeLineQuantity,
  lineIndex: number,
): LineItem | undefined => {
  const total = getChargeLineCostCents(quantity);

  if (!total) {
    return undefined;
  }

  const totalAmount = total.toString();

  const itemId = (lineIndex + 1).toString();

  const chargeQuantity = quantity.quantity.toString();

  return {
    '@Type': 'Procedure',
    PricingInd: 'Private',
    ChargeQuantity: chargeQuantity,
    ChargeCode: {
      '@Type': 'Procedure',
      Set: 'Tariff',
      Value: quantity.chargeLine.code,
      Description: quantity.chargeLine.name,
    },
    DOS: asHealthbridgeDate(dateOfService),
    DiagnosisCodes: diagnosisCodes,
    HospInd: 'Yes',
    LineItemFinancials: {
      BilledAmt: totalAmount,
      GrossAmt: totalAmount,
    },
    UniqueID: itemId,
    PlaceServiceCode: PlaceServiceCode.HomeCare,
  };
};

const getClaimLineItems = (
  dateOfService: string,
  diagnosisCodes: DiagnosisCode[],
  chargeLines: ClaimChargeLineQuantity[],
): LineItem[] | undefined => {
  const items = chargeLines.map((line, index) =>
    getClaimLineItem(dateOfService, diagnosisCodes, line, index),
  );

  if (!items.length) {
    return undefined;
  }

  const allItemsDefined = isFilled(items);

  if (!allItemsDefined) {
    return undefined;
  }

  return items;
};

export const updateChargeLines = (
  claim: WithId<fhir4.Claim>,
  chargeLines: ClaimChargeLineQuantity[],
  encounter: CreateReference,
): WithId<fhir4.Claim> => {
  const mapChargeLineToItem = partial(mapChargeLineToClaimItem, encounter);
  const item = valsOrNone(chargeLines.map(mapChargeLineToItem));

  return {
    ...claim,
    item,
  };
};

const mapRequiredCodingToDiagnosisCode = (
  type: 'Primary' | 'Secondary',
  code: RequiredCoding,
): DiagnosisCode => {
  return {
    Set: 'ICD',
    Version: '10',
    Type: type,
    Value: code.code,
    Description: code.display,
  };
};

const mapRequiredCodingToSecondaryDiagnosisCode = partial(
  mapRequiredCodingToDiagnosisCode,
  'Secondary',
);

const getDiagnosisCodes = (
  primaryIcd10Code: RequiredCoding,
  icd10Codes: RequiredCoding[] = [],
): DiagnosisCode[] | undefined => {
  const primaryDiagnosisCode = mapRequiredCodingToDiagnosisCode(
    'Primary',
    primaryIcd10Code,
  );
  const additionalDiagnosisCodes = icd10Codes.map<DiagnosisCode>(
    mapRequiredCodingToSecondaryDiagnosisCode,
  );

  return [primaryDiagnosisCode, ...additionalDiagnosisCodes];
};

const baseProvider: BaseProvider = {
  PracticeNum: '0904287',
  PracticeName: 'QM Partners',
  SpecialtyCode: '14',
  SubSpecialtyCode: '0',
};

const billingProvider: BillingProvider = {
  ...baseProvider,
  '@Type': 'Billing',
};

const treatingProvider: TreatingProvider = {
  ...baseProvider,
  '@Type': 'Treating',
  DispensingDoctor: 'No',
  HPCSA: '0904287',
};

export const providers: Providers = [billingProvider, treatingProvider];
export const pmaName = 'Insight' as const;
export const pmaVersion = '1' as const;

const ClaimMedicalAidMembershipSchema = MedicalAidSchema.pick({
  membershipNumber: true,
  schemeName: true,
});

type MedicalAidMembership = z.infer<typeof ClaimMedicalAidMembershipSchema>;

const ClaimPatientSchema = GeneralSchema.pick({
  birthDate: true,
  familyName: true,
  givenName: true,
  identifierValue: true,
});

type Patient = z.infer<typeof ClaimPatientSchema>;

const ClaimMainMemberSchema = GeneralSchema.pick({
  birthDate: true,
  familyName: true,
  givenName: true,
});

type MainMember = z.infer<typeof ClaimMainMemberSchema>;

const getMember = (
  membership: MedicalAidMembership,
  mainMember: MainMember,
): Member => ({
  DOB: asHealthbridgeDate(mainMember.birthDate),
  Name: mainMember.givenName,
  Surname: mainMember.familyName,
  SchemeName: membership.schemeName,
  MANum: membership.membershipNumber,
});

const getPatient = (
  patient: Patient,
  dependantCode: string,
): HealthbridgePatient => {
  const initials = patient.givenName[0];

  return {
    AccNum: patient.identifierValue,
    DOB: asHealthbridgeDate(patient.birthDate),
    DepCode: dependantCode,
    Name: patient.givenName,
    Surname: patient.familyName,
    Initials: initials,
    NewBornInd: 'No',
  };
};

const ClaimAdmissionSchema = AdmissionSchema.pick({
  period: true,
  primaryIcd10Code: true,
  icd10Codes: true,
});

const ClaimMembershipSchema = z.object({
  medicalAid: ClaimMedicalAidMembershipSchema,
  dependantCode: z.string(),
});

const ClaimChargeLinesSchema = z.object({
  chargeLines: ChargeLineQuantitySchema.array(),
});

export type PartialMapClaimToHealthbridgeRequestParams = Omit<
  FlatPartial<MapClaimToHealthbridgeRequestParams>,
  'membership'
> & {
  membership: {
    medicalAid: Partial<MedicalAid>;
    dependantCode?: string;
  };
};

export const ClaimToHealthbridgeRequestParamSchema = z.object({
  invoiceNumber: z.string().length(10).startsWith('QM'),
  transactionDate: z.string(),
  routingCode: z.string(),
  mainMember: ClaimMainMemberSchema,
  patient: ClaimPatientSchema,
  admission: ClaimAdmissionSchema,
  claim: ClaimChargeLinesSchema,
  membership: ClaimMembershipSchema,
});

export type MapClaimToHealthbridgeRequestParams = z.infer<
  typeof ClaimToHealthbridgeRequestParamSchema
>;

export function assertClaimToHealthbridgeRequestParamsValid(
  params: PartialMapClaimToHealthbridgeRequestParams,
): asserts params is MapClaimToHealthbridgeRequestParams {
  ClaimToHealthbridgeRequestParamSchema.parse(params);
}

enum MappingErrorMessage {
  invalidFinancials = 'Claim contains invalid financials',
  invalidDiagnosisCodes = 'Claim contains invalid diagnosis codes',
  invalidLineItems = 'Claim contains invalid line items',
}

interface MappingError {
  type: 'error';
  message: MappingErrorMessage;
}

interface MappingSuccess {
  type: 'success';
  result: CreateClaimRequest;
}

export type MappingResult = MappingError | MappingSuccess;

const getTransaction = (
  invoiceNumber: string,
  routingCode: string,
  transactionDate: string,
): Transaction => ({
  DataSet: '01',
  PMAName: pmaName,
  InvoiceNum: invoiceNumber,
  RoutingCode: routingCode,
  PMAVersion: pmaVersion,
  SavedDateTime: asHealthbridgeDateTime(transactionDate),
});

export const mapClaimToHealthbridgeRequest = ({
  admission,
  claim,
  mainMember,
  patient: patientGeneral,
  invoiceNumber,
  routingCode,
  transactionDate,
  membership,
}: MapClaimToHealthbridgeRequestParams): MappingResult => {
  const claimFinancials = getClaimFinancials(claim.chargeLines);

  const diagnosisCodes = getDiagnosisCodes(
    admission.primaryIcd10Code,
    admission.icd10Codes,
  );

  const member = getMember(membership.medicalAid, mainMember);
  const patient = getPatient(patientGeneral, membership.dependantCode);

  if (!claimFinancials) {
    return {
      type: 'error',
      message: MappingErrorMessage.invalidFinancials,
    };
  }

  if (!diagnosisCodes) {
    return {
      type: 'error',
      message: MappingErrorMessage.invalidDiagnosisCodes,
    };
  }

  const lineItems = getClaimLineItems(
    admission.period.start,
    diagnosisCodes,
    claim.chargeLines,
  );

  if (!lineItems) {
    return {
      type: 'error',
      message: MappingErrorMessage.invalidLineItems,
    };
  }

  const transaction = getTransaction(
    invoiceNumber,
    routingCode,
    transactionDate,
  );

  const result: CreateClaimRequest = {
    ProviderMessage: {
      '@Version': '6.0.0',
      '@xmlns:hb': 'healthbridge.co.za/messaging',
      Claim: {
        Details: {
          ClaimFinancials: claimFinancials,
          DiagnosisCodes: diagnosisCodes,
          LineItems: lineItems,
          COIDInd: 'No',
          HospInd: 'Yes',
          PayWho: 'Provider',
        },
        Member: member,
        Patient: patient,
        Providers: providers,
        Transaction: transaction,
      },
    },
  };

  return {
    type: 'success',
    result,
  };
};

export const markClaimAsActive = (
  claim: WithId<fhir4.Claim>,
  invoiceNumber: string,
): WithId<fhir4.Claim> => {
  const activeClaim: WithId<fhir4.Claim> = {
    ...claim,
    status: 'active',
  };

  return attachIdentifiers(
    activeClaim,
    createIdentifier(IdentifierCode.claimInvoiceNumber, invoiceNumber),
  );
};

export const setClaimResponse = (
  claim: WithId<fhir4.Claim>,
  claimResponse: CreateReference,
): WithId<fhir4.Claim> => {
  const { insurance } = claim;
  const insuranceWithResponse = insurance.map((ins) => ({
    ...ins,
    claimResponse,
  }));

  return {
    ...claim,
    insurance: insuranceWithResponse,
  };
};

export const generateClaimInvoiceNumber = () =>
  `QM${Math.random().toString().slice(2, 10)}`;
